<template>
  <div v-if="hasPaymentHistory">
    <content-box class="remain-pv-table--wrap">
      <div class="table--wrap" ref="listTable">
        <p class="content__title">{{ $t('userInfo.managePlan.planChart.title') }}</p>
        <ui-selectbox
          class="some-class"
          :value="duration"
          :items="durationList"
          :width="'175px'"
          @input="onDurationChange"
        />

        <app-pagination
          class="clearfix"
          :total="planStatusCount"
          :page="page"
          :limit="limit"
          @onPagingOptionChange="onPagingOptionChange" />

        <data-table class="pv-detail"
                    :headers="headers">
          <template v-slot:colgroup>
            <colgroup>
              <col width="16.6%">
              <col width="16.6%">
              <col width="16.6%">
              <col width="16.6%">
              <col width="16.6%">
              <col width="16.6%">
            </colgroup>
          </template>
          <template v-if="planStatus.length" v-slot>
            <tbody>
              <tr v-for="(item, index) in planStatusList" :key="index" :class="{'up':item.chargeShow, 'charge-plan' : item.bPVCharge, 'on' : item.show}" :data-plan-id="item.useEndDate">
                <td class="data-plan">
                  <i v-if="item.bPVCharge" class="icon icon--arrow-gray-size12"></i>
                  <span v-if="item.planName === 'PV 충전'">
                    {{ $t('common.chargePv') }}
                  </span>
                  <span v-else>{{ item.planName }}</span>
                  <i v-if="item.existPVCharge" class="icon icon--toggle-slide16" :class="{'up':item.chargeShow}" @click="toggleCharge(item, $event)" ></i>
                </td>
                <td class="data-date">{{ item.useStartDate | dashDate }} ~ {{ item.useEndDate | dashDate }}</td>
                <td class="data-provide"><span v-if="item.bPVCharge">+</span> <span :class="{'charge-pv' : item.existPVCharge}">{{ item.uiChargePV | thousand }} PV</span></td>
                <td class="data-remain">
                  <span v-if="!item.bPVCharge">
                    {{ item.uiChargePV - item.remainPV | thousand }} PV
                  </span>
                  <span v-else>
                    -
                  </span>
                </td>
                <td class="data-method">
                  <span v-if="item.paymentType === 'card'">
                    {{ $t('userInfo.managePlan.default.card') }}
                  </span>
                  <span v-else-if="item.paymentType === 'account' || item.paymentType === 'wire transfer'">
                    {{ $t('userInfo.managePlan.default.wire') }}
                  </span>
                  <span v-else-if="item.paymentType === 'coupon'">
                    {{ $t('common.coupon') }}
                  </span>
                  <span v-else-if="item.paymentType === '4grit' || item.paymentType === 'admin'">
                    {{ $t('common.admin') }}
                  </span>
                  <span v-else>{{ item.paymentType }}</span>
                </td>
                <td class="data-total">{{ item.payment | thousand }}</td>
              </tr>
            </tbody>
          </template>
          <tr v-else>
            <td class="no-data" colspan="6">
              <p v-html="$t('common.noRecord')"></p>
            </td>
          </tr>
        </data-table>
      </div>
    </content-box>
  </div>
</template>

<script>
import ContentBox from '@/components/ui/ContentBox'
import AppPagination from '@/components/ui/AppPagination'
import UiSelectbox from '@/components/ui/UiSelectbox'
import DataTable from '@/components/ui/DataTable'
import { mapState } from 'vuex'
import { parse, format, startOfDay, addDays, addMonths } from 'date-fns'
import { EventBus } from '@/libs/event-bus'
import { planHistory } from '@/api/user'
import { getTooltipHtml } from '@/libs/chart-helper'
import { thousand, getLocaleDateStr } from '@/libs/utils'

export default {
  components: {
    ContentBox,
    AppPagination,
    UiSelectbox,
    DataTable
  },
  props: [
    'startDate'
  ],
  data () {
    return {
      isLoading: true,
      hasPaymentHistory: false,
      minDate: null,
      maxDate: null,
      dateRange: {
        startDate: null,
        endDate: null
      },
      showDateRangePicker: false,
      // pv table
      headers: [
        {
          name: this.$t('common.table.header.planName')
        },
        {
          name: this.$t('common.table.header.servicePeroid')
        },
        {
          name: this.$t('common.table.header.providedPV')
        },
        {
          name: this.$t('common.table.header.userPV')
        },
        {
          name: this.$t('common.table.header.paymentMethod')
        },
        {
          name: this.$t('common.table.header.totalKRW')
        }
      ],
      page: 1,
      limit: 30,
      planStatus: [],
      chartType: 'remainPV',
      duration: 0,
      durationList: [
        {
          value: 0,
          text: this.$t('common.all')
        },
        {
          value: 3,
          text: this.$t('common.recentMonths', { month: '3' })
        },
        {
          value: 6,
          text: this.$t('common.recentMonths', { month: '6' })
        },
        {
          value: 12,
          text: this.$t('common.recentMonths', { month: '12' })
        },
        {
          value: 24,
          text: this.$t('common.recentMonths', { month: '24' })
        }
      ]
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData'
    ]),
    planStatusCount () {
      return this.planStatus ? this.planStatus.length : 0
    },
    planStatusList: {
      get: function () {
        if (!this.planStatus) {
          return []
        }
        const startIndex = (this.page - 1) * this.limit
        const endIndex = startIndex + this.limit
        return this.planStatus.slice(startIndex, endIndex)
      },
      set: function () {

      }
    },
    dateRangeStr () {
      return `${getLocaleDateStr(this.dateRange.startDate)} - ${getLocaleDateStr(this.dateRange.endDate)}`
    },
    chartOptions () {
      const pvData = this.planStatus.map(item => {
        return [
          format(parse(item.date), 'YYYY-MM-DD'),
          item[this.chartType] > 0 ? item[this.chartType] : 0
        ]
      })

      const options = {
        meta: {
          isDailyChart: true
        },
        grid: {
          left: 10,
          right: 35
        },
        series: [{
          name: 'PV',
          type: 'line',
          data: pvData,
          showSymbol: false,
          symbolSize: 2,
          smooth: true
        }],
        tooltip: {
          formatter (params) {
            const item = params[0]
            const title = format(parse(item.axisValue), 'YYYY-MM-DD')
            let seriesName = item.seriesName
            let value = item.value[1]
            const content = [
              [
                `${item.marker}${seriesName} : `,
                thousand(value)
              ]
            ]
            return getTooltipHtml(content, title)
          }
        },
        xAxis: {
          // type: 'category',
          boundaryGap: false,
          axisLabel: {
            formatter (value) {
              return format(parse(value), 'YYYY-MM-DD')
            }
          }
        },
        yAxis: {
          type: 'value',
          splitNumber: 2,
          axisLabel: {}
        },
        color: '#EC0047'
      }
      return options
    }
  },
  created () {
    const today = startOfDay(new Date())
    const minDate = addDays(startOfDay(parse(this.userData.regDate)), -1)
    const maxDate = parse(today)
    const endDate = parse(today)
    let startDate = minDate

    if (startDate < minDate) {
      startDate = parse(minDate)
    }

    this.hasPaymentHistory = true
    this.minDate = minDate
    this.maxDate = maxDate

    this.dateRange = {
      startDate,
      endDate
    }
    this.loadData()
  },
  mounted () {
    EventBus.$on('closeDateRangePicker', () => {
      this.showDateRangePicker = false
    })
  },
  methods: {
    toggleCharge (data, $event) {
      data.chargeShow = !data.chargeShow
      this.planStatusList = this.planStatusList.map(function (item) {
        const useEndDate = data.useEndDate

        if (item.bPVCharge && item.useEndDate === useEndDate) {
          item.show = !item.show
        }
      })
    },
    loadData () {
      this.isLoading = true
      const startDate = this.dateRange.startDate
      const endDate = this.dateRange.endDate

      if (startDate && endDate) {
        const start = format(startDate, 'YYYY-MM-DD')
        const end = format(endDate, 'YYYY-MM-DD')
        planHistory(start, end).then(data => {
          let chargeList = data.charge
          let planList = data.plan
          let planUseList = []

          chargeList = chargeList.map(function (item) {
            item.bPVCharge = true
            item.show = false
            item.uiChargePV = item.chargePV
            return item
          })

          for (let i = 0, iLen = planList.length; i < iLen; i++) {
            let item = planList[i]
            item.chargeShow = false
            item.uiChargePV = item.chargePV
            let tempChargeList = chargeList.filter(chargeItem => chargeItem.useEndDate === item.useEndDate)
            if (tempChargeList.length > 0) {
              item['existPVCharge'] = true
              item['pvChargeCount'] = tempChargeList.length

              tempChargeList = tempChargeList.map(function (subItem) {
                item.uiChargePV += subItem.chargePV
                return subItem
              })
            }

            planUseList.push(item)
            if (tempChargeList.length > 0) {
              planUseList = planUseList.concat(tempChargeList)
            }
          }

          this.planStatus = planUseList
          this.isLoading = false
        }).catch(() => {
          this.planStatus = []
          this.isLoading = false
        })
      } else {
        this.planStatus = []
        this.isLoading = false
      }
    },
    togglePickerClick () {
      EventBus.$emit('closeAllUiSelectBox')
      EventBus.$emit('closeUserMenu')
      EventBus.$emit('closeReferrerList')
      EventBus.$emit('closeReportPageList')
      this.showDateRangePicker = !this.showDateRangePicker
    },
    closePicker () {
      this.showDateRangePicker = false
    },
    onRangeCancel () {
      this.closePicker()
    },
    onRangeChange (data) {
      this.dateRange = data.range
      this.loadData()
      this.closePicker()
    },
    onPagingOptionChange (page, limit) {
      this.page = page
      this.limit = limit
    },
    onDurationChange (duration) {
      const today = startOfDay(new Date())
      const minDate = addDays(startOfDay(parse(this.userData.regDate)), -1)
      const endDate = parse(today)
      let startDate = addMonths(today, -duration)
      if (duration === 0) {
        startDate = addDays(parse(this.userData.regDate), -1)
      }

      if (startDate < minDate) {
        startDate = parse(minDate)
      }

      this.duration = duration

      this.onRangeChange({
        range: {
          startDate,
          endDate
        }
      })
      this.loadData()
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
  .remain-pv--wrap,
  .remain-pv-table--wrap {
    padding: 15px 25px 30px 25px;
    box-sizing: border-box;
    margin-top: 30px;
  }

  .remain-pv--wrap {
    .content__title {
      font-size: 15px;
      font-weight: bold;
      color: #2f2f2f;
      display: inline-block;
      padding: 8px 15px 20px 0;
    }

    .content__label {
      color: #444444;
      font-size: 14px;
      margin-left: 20px;

      .icon {
        vertical-align: middle;
        transform: translateY(-1px);
        margin-right: 3px;
      }
    }

    .no-data {
      height: 100px;
      position: relative;
      &> p {
        text-align: center;
        @include positionYcenter;
      }
    }

    .date-range-picker--wrap {
      position: relative;
      float: right;
      display: inline-block;
      margin-left: 10px;
      input {
        position: relative;
        width: 215px;
        height: 30px;
        border-radius: 8px;
        border: solid 1px #e0e0e0;
        background-color: #ffffff;
        padding-left: 10px;
        color: #767676;
      }
      .icon--calendar {
        position: absolute;
        top: 50%;
        right: 8px;
        transform: translateY(-50%);
        pointer-events: none;
      }
      /deep/ .date_range_picker {
        top: 50px;
        left: -60px;
      }
    }
  }

  .remain-pv-table--wrap {
    .content {
      text-align: center;
    }
    .content__title {
      font-size: 15px;
      font-weight: bold;
      color: #2f2f2f;
      display: inline-block;
      padding: 11px 0 20px 0;
    }

    .ui_select {
      vertical-align: middle;
      transform: translateY(-2px);
      margin-left: 10px;
      position: relative;
      z-index: 2;
    }

    .page__comm--wrap {
      float: right;
      width: auto;
      margin-bottom: 15px;
    }

    .table--wrap {
      tbody tr {
        border-top: 1px solid #ebebeb;
        text-align: center;

        &:first-child {
          border-top: none
        }

        &:last-child {
          border-bottom: 1px solid #ebebeb;
        }

        td {
          height: 50px;
          font-size: 13px;
          line-height: 1.23;
          color: #2f2f2f;
          position: relative;
        }

        &.charge-plan {
          display: none;

          & > .data-plan {
            padding-left: 15px;
            color: #1199af;
          }

          & > .data-date {
            color: #1199af;
          }

          & > .data-provide {
            color: #1199af;
          }

          &.on {
            display: table-row;
            border-top: 0px solid #eeeeee;
          }
          & > td {
            height: 36px;
          }
        }

        &.up {
          border-bottom: none;
        }
      }
    }
  }

  .no-data {
    height: 100px;
    padding-top: 15px;
    position: relative;
    &> p {
      text-align: center;
    }
  }

  .icon--toggle-slide16 {
    position: absolute;
    top: 17px;
    margin-left: 5px;
    width: 16px;
    height: 16px;
    background: url(../../../assets/img/icon_slide_toggle_size16.svg) no-repeat 0 0;
    cursor: pointer;
    pointer-events: visible;
    &.up {
      transform: rotate(180deg);
    }
  }

  .icon--arrow-gray-size12 {
    position: relative;
    padding-right: 5px;
    top: 1px;
  }

  .charge-pv {
    color: #1199af;
  }
</style>
